import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2, Mail } from 'lucide-react'
import { translateApiError } from '../../lib/apiErrors'
import { pollCheckoutStatusAndTrackPurchase } from '../../lib/credits/pollCheckoutStatus'
import { useToastStore } from '../../store/toastStore'
import { useAuthStore } from '../../store/authStore'
import AuthBrand from '../../components/auth/AuthBrand'
import AuthShell from '../../components/auth/AuthShell'
import { authSubmitClass } from '../../components/auth/authTheme'
import { AppIcon } from '../../components/icons/AppIcon'

type PaymentAction =
  | 'existing_user'
  | 'redirect_to_register'
  | 'authenticated_user'
  | 'check_email'
  | null

export default function PaymentSuccess() {
  const { t } = useTranslation(['subscriptions', 'errors'])
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [action, setAction] = useState<PaymentAction>(null)
  const [creditsToken, setCreditsToken] = useState<string | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const navigate = useNavigate()
  const { addToast } = useToastStore()
  const { isAuthenticated, loadUserPermissions } = useAuthStore()

  useEffect(() => {
    const sessionId = searchParams.get('session_id')

    if (!sessionId) {
      addToast(t('subscriptions:paymentSuccess.noSessionId'), 'error')
      navigate('/auth/login')
      return
    }

    let cancelled = false

    const run = async () => {
      try {
        const data = await pollCheckoutStatusAndTrackPurchase(sessionId, {
          shouldContinue: () => !cancelled,
        })

        if (cancelled) return

        if (data.status === 'completed') {
          if (data.action === 'redirect_to_register') {
            setAction('redirect_to_register')
            setCreditsToken(data.credits_token ?? null)
            setEmail(data.email ?? null)
          } else if (isAuthenticated) {
            setAction('authenticated_user')
            addToast(t('subscriptions:paymentSuccess.authenticatedUser.moduleAdded'), 'success')
            await loadUserPermissions(true)
            if (!cancelled) {
              setTimeout(() => navigate('/credits'), 1500)
            }
          } else {
            setAction((data.action as PaymentAction) ?? 'existing_user')
            setCreditsToken(data.credits_token ?? null)
            setEmail(data.email ?? null)
          }
        } else if (data.status === 'processing') {
          setAction('check_email')
        } else {
          addToast(t('subscriptions:paymentSuccess.paymentNotCompleted'), 'error')
          navigate('/')
          return
        }
      } catch (error) {
        if (cancelled) return
        addToast(translateApiError(error, t), 'error')
        navigate('/')
        return
      }

      if (!cancelled) {
        setLoading(false)
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [searchParams])

  const handleContinueToRegistration = () => {
    if (creditsToken && email) {
      navigate(
        `/auth/register-after-payment?token=${creditsToken}&email=${encodeURIComponent(email)}`,
      )
    }
  }

  if (loading) {
    return (
      <AuthShell>
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#FF5A00] mx-auto mb-4" />
          <p className="text-gray-400">{t('subscriptions:paymentSuccess.checking')}</p>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <div className="text-center">
        <AuthBrand />

        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-900/40 border border-green-700 mb-4">
          <AppIcon name="check" size={28} weight="bold" className="text-green-400" />
        </div>

        <h1 className="text-3xl font-bold text-white mb-2">
          {t('subscriptions:paymentSuccess.title')}
        </h1>

        <div className="mt-6">
          {action === 'check_email' && (
            <>
              <Mail className="h-10 w-10 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400 mb-6">
                {t('subscriptions:paymentSuccess.checkEmail.body')}
              </p>
            </>
          )}

          {action === 'authenticated_user' && (
            <>
              <p className="text-gray-400 mb-6">
                {t('subscriptions:paymentSuccess.authenticatedUser.body')}
              </p>
              <button onClick={() => navigate('/subscriptions')} className={authSubmitClass}>
                {t('subscriptions:paymentSuccess.authenticatedUser.goToSubscriptions')}
              </button>
            </>
          )}

          {action === 'existing_user' && (
            <>
              <p className="text-gray-400 mb-6">
                {t('subscriptions:paymentSuccess.existingUser.body')}
              </p>
              <button onClick={() => navigate('/auth/login')} className={authSubmitClass}>
                {t('subscriptions:paymentSuccess.existingUser.login')}
              </button>
            </>
          )}

          {action === 'redirect_to_register' && (
            <>
              <p className="text-gray-400 mb-6">
                {t('subscriptions:paymentSuccess.redirectToRegister.body')}
              </p>
              <button onClick={handleContinueToRegistration} className={authSubmitClass}>
                {t('subscriptions:paymentSuccess.redirectToRegister.completeRegistration')}
              </button>
            </>
          )}
        </div>

        <p className="text-sm text-gray-500 mt-6">
          {action === 'redirect_to_register'
            ? t('subscriptions:paymentSuccess.redirectToRegister.footer')
            : action === 'authenticated_user'
              ? t('subscriptions:paymentSuccess.authenticatedUser.footer')
              : action === 'check_email'
                ? t('subscriptions:paymentSuccess.checkEmail.footer')
                : t('subscriptions:paymentSuccess.existingUser.footer')}
        </p>
      </div>
    </AuthShell>
  )
}
